import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-telemetry-auth',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="auth-container">
      <div class="auth-card">
        <h2>Connect Telemetry Client</h2>
        <p *ngIf="!error && !completed">
          We are securely connecting your browser login to the telemetry app.
        </p>
        <p class="success-message" *ngIf="completed">
          Login approved. You can close this tab now. If the app did not open automatically,
          use the button below.
        </p>
        <p class="error-message" *ngIf="error">{{ error }}</p>

        <a class="open-button" *ngIf="deepLinkUrl" [href]="deepLinkUrl">
          Open Telemetry Client
        </a>
        <button class="close-button" *ngIf="completed" (click)="closeTab()">
          Close This Tab
        </button>

        <a class="login-link" *ngIf="showLoginLink" [routerLink]="['/login']" [queryParams]="loginQueryParams">
          Continue to Login
        </a>
      </div>
    </div>
  `,
  styles: [`
    .auth-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      background-color: #121212;
      color: #fff;
      padding: 16px;
    }

    .auth-card {
      background-color: #1e1e1e;
      padding: 2rem;
      border-radius: 8px;
      width: 100%;
      max-width: 460px;
      text-align: center;
      border: 1px solid #2f2f2f;
    }

    h2 {
      margin: 0 0 1rem;
      color: #e5e7eb;
    }

    p {
      margin: 0 0 1rem;
      color: #c7c7c7;
    }

    .open-button {
      display: inline-block;
      margin-top: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 6px;
      text-decoration: none;
      font-weight: 600;
      color: #0b1220;
      background: #22c55e;
    }

    .close-button {
      display: inline-block;
      margin-top: 0.75rem;
      padding: 0.65rem 1rem;
      border-radius: 6px;
      border: 1px solid #3f3f46;
      background: #18181b;
      color: #e4e4e7;
      cursor: pointer;
    }

    .error-message {
      color: #f87171;
    }

    .success-message {
      color: #34d399;
    }

    .login-link {
      display: inline-block;
      margin-top: 1rem;
      color: #a78bfa;
      text-decoration: none;
    }
  `],
})
export class TelemetryAuthComponent implements OnInit {
  error = '';
  completed = false;
  deepLinkUrl = '';
  showLoginLink = false;
  loginQueryParams: Record<string, string> = {};

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.authService.ready();

    const state = this.route.snapshot.queryParamMap.get('state') || '';
    const callbackUrl = this.getCallbackUrl();
    const callbackScheme = this.getCallbackScheme();
    const returnUrlParams = new URLSearchParams();
    if (state) {
      returnUrlParams.set('state', state);
    }
    if (callbackUrl) {
      returnUrlParams.set('callbackUrl', callbackUrl);
    }
    if (callbackScheme) {
      returnUrlParams.set('callbackScheme', callbackScheme);
    }
    const returnUrl = returnUrlParams.toString()
      ? `/telemetry-auth?${returnUrlParams.toString()}`
      : '/telemetry-auth';

    if (!this.authService.isAuthenticated()) {
      this.showLoginLink = true;
      this.loginQueryParams = { returnUrl };
      await this.router.navigate(['/login'], { queryParams: { returnUrl } });
      return;
    }

    try {
      const response = await firstValueFrom(this.authService.createTelemetryClientAuthCode());
      const params = new URLSearchParams({ code: response.code });
      if (state) {
        params.set('state', state);
      }
      if (callbackUrl) {
        const target = new URL(callbackUrl);
        target.searchParams.set('code', response.code);
        if (state) {
          target.searchParams.set('state', state);
        }
        this.deepLinkUrl = target.toString();
      } else {
        this.deepLinkUrl = `${callbackScheme}://auth/callback?${params.toString()}`;
      }
      this.completed = true;

      // Attempt automatic handoff first; browser may require user interaction.
      window.location.href = this.deepLinkUrl;
      setTimeout(() => this.closeTab(), 1200);
    } catch (error: any) {
      this.error = error?.error?.error || error?.message || 'Failed to start telemetry login handoff.';
    }
  }

  closeTab(): void {
    window.close();
  }

  private getCallbackScheme(): string {
    const rawScheme = (this.route.snapshot.queryParamMap.get('callbackScheme') || '')
      .trim()
      .toLowerCase();
    if (rawScheme && /^[a-z][a-z0-9+.-]*$/.test(rawScheme)) {
      return rawScheme;
    }
    return 'spectrace';
  }

  private getCallbackUrl(): string {
    const rawUrl = (this.route.snapshot.queryParamMap.get('callbackUrl') || '').trim();
    if (!rawUrl) {
      return '';
    }

    try {
      const parsed = new URL(rawUrl);
      const isLocalhost =
        parsed.protocol === 'http:' &&
        (parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost');
      const isExpectedPath = parsed.pathname === '/auth/callback';
      if (isLocalhost && isExpectedPath) {
        return parsed.toString();
      }
    } catch {
      return '';
    }

    return '';
  }
}
